import { useFormContext, type FieldValues } from "react-hook-form";
import { TemplateWeekdaysPicker } from "./TemplateWeekdaysPicker";
import { TemplateRecurrencePicker } from "./TemplateRecurrencePicker";
import {
  activityFrequencySchema,
  recurringOptionSchema,
} from "./schedulingModels";
import { TemplateTimeSlotsPicker } from "./TemplateTimeSlotsPicker";
import { useEffect } from "react";
import { TemplateFrequencyPicker } from "./TemplateFrequencyPicker";
import { TemplateCustomRecurringInterval } from "./TemplateCustomRecurringInput";
import { useFieldPathWithPrefix } from "@/forms/FormFieldPathPrefixContext";
import type { TemplateSchedulingFields } from "../../templateFormSchema";

export const TemplateScheduling = () => {
  const { unregister, watch } = useFormContext<FieldValues>();

  const recurrenceFieldName =
    useFieldPathWithPrefix<TemplateSchedulingFields>("recurrence");
  const frequencyFieldName =
    useFieldPathWithPrefix<TemplateSchedulingFields>("frequency");
  const weekdaysFieldName =
    useFieldPathWithPrefix<TemplateSchedulingFields>("weekdays");
  const customRecurrenceIntervalFieldName =
    useFieldPathWithPrefix<TemplateSchedulingFields>(
      "customRecurrenceInterval",
    );
  const customRecurrenceIntervalUnitFieldName =
    useFieldPathWithPrefix<TemplateSchedulingFields>(
      "customRecurrenceIntervalUnit",
    );

  const isRecurringOnWeekdays =
    watch(recurrenceFieldName) ===
    recurringOptionSchema.enum.onSelectedWeekdays;

  const isCustomRecurring =
    watch(recurrenceFieldName) === recurringOptionSchema.enum.custom;

  const selectedRecurring = watch(frequencyFieldName);
  const isRecurring =
    selectedRecurring === activityFrequencySchema.enum.recurring;

  // Unregister the weekdays field if the activity is not recurring on weekdays
  useEffect(() => {
    if (!isRecurringOnWeekdays) {
      unregister(weekdaysFieldName);
    }
  }, [isRecurringOnWeekdays, unregister, weekdaysFieldName]);

  // Unregister the recurrence field if the activity is one-time
  useEffect(() => {
    if (selectedRecurring === activityFrequencySchema.enum.oneTime) {
      unregister(recurrenceFieldName);
    }
  }, [selectedRecurring, unregister, recurrenceFieldName]);

  // Unregister the custom recurring interval field if the activity is not custom recurring
  useEffect(() => {
    if (!isCustomRecurring) {
      unregister(customRecurrenceIntervalFieldName);
      unregister(customRecurrenceIntervalUnitFieldName);
    }
  }, [
    isCustomRecurring,
    unregister,
    customRecurrenceIntervalFieldName,
    customRecurrenceIntervalUnitFieldName,
  ]);

  return (
    <>
      <TemplateFrequencyPicker />
      {isRecurring ? (
        <>
          <TemplateRecurrencePicker />
          {isRecurringOnWeekdays ? <TemplateWeekdaysPicker /> : <></>}
          {isCustomRecurring ? <TemplateCustomRecurringInterval /> : <></>}
        </>
      ) : null}
      <TemplateTimeSlotsPicker isRecurring={isRecurring} />
    </>
  );
};
